import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { AxiosInterceptor } from "./services/axiosIntercept"; // Adjust path if needed

import RequireAuth from "./Components/RequireAuth/RequireAuth";
import LandingPage from "./Pages/Landing/landing_page";
import WizardListPage from "./features/wizard-cms/pages/WizardListPage";
import EditWizardPage from "./features/wizard-cms/pages/EditWizardPage";
import DashboardLayout from "./Components/layout/DashboardLayout";
import OverviewPage from "./Pages/OverviewPage";
import CreateWizardPage from "./features/wizard-cms/pages/CreateWizardPage";
import AuthDebug from "./Components/AuthDebug/authDebug";

/**
 * This component contains the core application logic and routing,
 * which is dependent on the authentication state.
 * @returns {JSX.Element | null}
 */
const AppRoutes: React.FC = () => {
  const { user, isLoading } = useAuth();

  // A null return provides a brief blank screen during the initial,
  // fast authentication check, which is a smoother user experience
  // than a flashing loading indicator.
  if (isLoading) {
    return null;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={!user ? <LandingPage /> : <Navigate to="/" />}
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        {/* Nested routes will render inside DashboardLayout's <Outlet> */}
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OverviewPage />} />
        <Route path="wizards" element={<WizardListPage />} />
        <Route path="wizards/new" element={<CreateWizardPage />} />
        <Route path="wizards/:wizardId/edit" element={<EditWizardPage />} />
        <Route path="debug" element={<AuthDebug />} />
      </Route>
      <Route path="*" element={<div>404: Page not found</div>} />
    </Routes>
  );
};

/**
 * The main App component, which sets up the application's top-level providers
 * for authentication, API handling, and routing.
 * @returns {JSX.Element}
 */
const App: React.FC = () => (
  <AxiosInterceptor>
    <AppRoutes />
  </AxiosInterceptor>
);

export default App;
